<template>
  <v-navigation-drawer
    v-model="panel.visible"
    v-click-outside="clickedOutside"
    fixed
    right
    stateless
    disable-resize-watcher
    temporary
    width="512"
    @input="panelInputChanged"
  >
    <v-container>
      <h3 class="text-center">Edycja spotkania: {{ meeting.name }}</h3>
      <v-divider class="my-4" />
      <v-form v-if="meeting && loaded" ref="form">
        <v-text-field
          v-model="form.name"
          label="Nazwa spotkania"
          name="name"
          type="text"
          :error-messages="nameErrors"
          @input="$v.form.name.$touch()"
          @blur="$v.form.name.$touch()"
          @keydown.enter="submit"
        />
        <v-row>
          <v-col>
            <v-menu
              v-model="dateFromDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="dateFromFormatted"
                  label="Data rozpoczęcia"
                  readonly
                  :error-messages="dateFromDateErrors"
                  v-on="on"
                  @input="$v.form.dateFrom.$touch()"
                  @blur="$v.form.dateFrom.$touch()"
                  @keydown.enter="submit"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.dateFrom"
                no-title
                locale="pl-PL"
                @input="dateFromDateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col>
            <v-menu
              ref="menu1"
              v-model="dateFromTimeMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              :return-value.sync="form.dateFromTime"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="form.dateFromTime"
                  label="Godzina rozpoczęcia"
                  readonly
                  :error-messages="dateFromTimeErrors"
                  v-on="on"
                  @input="$v.form.dateFromTime.$touch()"
                  @blur="$v.form.dateFromTime.$touch()"
                  @keydown.enter="submit"
                ></v-text-field>
              </template>
              <v-time-picker
                v-if="dateFromTimeMenu"
                v-model="form.dateFromTime"
                full-width
                format="24hr"
                @click:minute="$refs.menu1.save(form.dateFromTime)"
              ></v-time-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-menu
              v-model="dateToDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="dateToFormatted"
                  label="Data zakończenia"
                  readonly
                  :error-messages="dateToDateErrors"
                  v-on="on"
                  @input="$v.form.dateTo.$touch()"
                  @blur="$v.form.dateTo.$touch()"
                  @keydown.enter="submit"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.dateTo"
                no-title
                locale="pl-PL"
                @input="dateToDateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col>
            <v-menu
              ref="menu2"
              v-model="dateToTimeMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              :return-value.sync="form.dateToTime"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="form.dateToTime"
                  label="Godzina zakończenia"
                  readonly
                  :error-messages="dateToTimeErrors"
                  v-on="on"
                  @input="$v.form.dateToTime.$touch()"
                  @blur="$v.form.dateToTime.$touch()"
                  @keydown.enter="submit"
                ></v-text-field>
              </template>
              <v-time-picker
                v-if="dateToTimeMenu"
                v-model="form.dateToTime"
                full-width
                format="24hr"
                @click:minute="$refs.menu2.save(form.dateToTime)"
              ></v-time-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-checkbox v-model="form.isRequired" label="Wymagane do zaliczenia"></v-checkbox>
      </v-form>
      <v-divider class="my-4" />
      <v-row align="center" justify="center">
        <v-col>
          <v-btn class="action-btn" color="success" block :loading="loading" @click="submit">Zapisz zmiany</v-btn>
        </v-col>
        <v-col>
          <v-dialog v-model="dialog" persistent max-width="400">
            <template v-slot:activator="{ on }">
              <v-btn
                class="action-btn"
                color="error"
                block
                :disabled="loading"
                v-on="formChanged ? on : { click: cancel }"
                >Anuluj</v-btn
              >
            </template>
            <v-card>
              <v-toolbar color="error" dark flat>
                <v-toolbar-title>Ostrzeżenie</v-toolbar-title>
              </v-toolbar>
              <v-card-text class="mt-4">Czy na pewno chcesz anulować wprowadzone zmiany?</v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn class="text--secondary" text @click="cancelWarning">Nie</v-btn>
                <v-btn color="error" text @click="cancel">Tak</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>
    </v-container>
  </v-navigation-drawer>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import * as moment from 'moment';
import { minDateTime } from '@/plugins/vuelidate/validators';
import ClickOutside from 'vue-click-outside';
import { createNamespacedHelpers } from 'vuex';

const { mapActions, mapState } = createNamespacedHelpers('courses');

export default {
  directives: {
    ClickOutside
  },
  mixins: [validationMixin],
  data: function() {
    return {
      form: {
        name: '',
        dateFrom: null,
        dateFromTime: null,
        dateTo: null,
        dateToTime: null,
        isRequired: true
      },
      dialog: false,
      dateFromDateMenu: false,
      dateFromTimeMenu: false,
      dateToDateMenu: false,
      dateToTimeMenu: false,
      loaded: false,
      loading: false,
      panel: {
        visible: true,
        firstLoad: false
      },
      clickedOutsideEnabled: true
    };
  },
  computed: {
    ...mapState({
      meeting: state => state.activeCourseEditTree.activeElement.element,
      courseId: state => state.activeCourseEditTree.course.id,
      definition: state => state.activeCourseEditTree.activeElement.definition
    }),
    dateFromFormatted() {
      return this.form.dateFrom ? moment(this.form.dateFrom).format('DD.MM.YYYY') : '';
    },
    dateToFormatted() {
      return this.form.dateTo ? moment(this.form.dateTo).format('DD.MM.YYYY') : '';
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.form.name.$dirty) {
        return errors;
      }
      if (!this.$v.form.name.required) {
        errors.push('Nazwa spotkania jest wymagana');
      }
      return errors;
    },
    dateFromDateErrors() {
      const errors = [];
      if (!this.$v.form.dateFrom.$dirty) {
        return errors;
      }
      if (!this.$v.form.dateFrom.required) {
        errors.push('Data rozpoczęcia jest wymagana');
      }
      return errors;
    },
    dateFromTimeErrors() {
      const errors = [];
      if (!this.$v.form.dateFromTime.$dirty) {
        return errors;
      }
      if (!this.$v.form.dateFromTime.required) {
        errors.push('Godzina rozpoczęcia jest wymagana');
      }
      return errors;
    },
    dateToDateErrors() {
      const errors = [];
      if (!this.$v.form.dateTo.$dirty) {
        return errors;
      }
      if (!this.$v.form.dateTo.required) {
        errors.push('Data zakończenia jest wymagana');
      }
      if (!this.$v.form.dateTo.minDateTime) {
        errors.push('Zakończenie nie może być wcześniejsze niż rozpoczęcie');
      }
      return errors;
    },
    dateToTimeErrors() {
      const errors = [];
      if (!this.$v.form.dateToTime.$dirty) {
        return errors;
      }
      if (!this.$v.form.dateToTime.required) {
        errors.push('Godzina zakończenia jest wymagana');
      }
      return errors;
    },
    formValid() {
      return !this.$v.form.$invalid && !this.$v.form.$pending;
    },
    formChanged() {
      return this.$v.$anyDirty;
    }
  },
  watch: {
    dateFromDateMenu(val) {
      this.panel.firstLoad = false;
      this.clickedOutsideEnabled = !val;
    },
    dateFromTimeMenu(val) {
      this.clickedOutsideEnabled = !val;
    },
    dateToDateMenu(val) {
      this.panel.firstLoad = false;
      this.clickedOutsideEnabled = !val;
    },
    dateToTimeMenu(val) {
      this.clickedOutsideEnabled = !val;
    }
  },
  async mounted() {
    this.form.name = this.meeting.name;
    this.form.isRequired = this.meeting.isRequired;
    if (!this.meeting.newDefinition) {
      if (this.meeting.id === -1) {
        this.loaded = true;
        return;
      }

      await this.requestElementDefinition({ id: this.meeting.id });
      this.form.dateFrom = moment(this.definition.dateFrom).format('YYYY-MM-DD');
      this.form.dateFromTime = moment(this.definition.dateFrom).format('HH:mm');
      this.form.dateTo = moment(this.definition.dateTo).format('YYYY-MM-DD');
      this.form.dateToTime = moment(this.definition.dateTo).format('HH:mm');
      this.loaded = true;
    } else {
      this.form.dateFrom = moment(this.meeting.newDefinition.dateFrom).format('YYYY-MM-DD');
      this.form.dateFromTime = moment(this.meeting.newDefinition.dateFrom).format('HH:mm');
      this.form.dateTo = moment(this.meeting.newDefinition.dateTo).format('YYYY-MM-DD');
      this.form.dateToTime = moment(this.meeting.newDefinition.dateTo).format('HH:mm');
      this.loaded = true;
    }
  },
  validations: {
    form: {
      name: {
        required
      },
      dateFrom: {
        required
      },
      dateFromTime: { required },
      dateTo: {
        required,
        minDateTime: minDateTime('dateFrom', 'dateFromTime', 'dateToTime')
      },
      dateToTime: { required }
    }
  },
  methods: {
    ...mapActions(['saveMeeting', 'resetActiveElement', 'requestElementDefinition']),
    async submit() {
      this.$v.$touch();

      if (this.formValid) {
        this.loading = true;
        await this.saveMeeting({
          courseId: this.courseId,
          instance: this.meeting,
          name: this.form.name,
          isRequired: this.form.isRequired,
          definition: this.definition,
          newDefinition: {
            dateFrom: moment(`${this.form.dateFrom} ${this.form.dateFromTime}`, 'YYYY-MM-DD HH:mm').toISOString(true),
            dateTo: moment(`${this.form.dateTo} ${this.form.dateToTime}`, 'YYYY-MM-DD HH:mm').toISOString(true)
          }
        });
        this.loading = false;
      }
    },
    cancel() {
      this.dialog = false;
      this.resetActiveElement();
    },
    clickedOutside() {
      if (this.clickedOutsideEnabled) {
        if (this.panel.firstLoad) {
          if (this.formChanged) {
            this.dialog = true;
          } else {
            this.cancel();
          }
        }
      }

      this.panel.firstLoad = true;
    },
    panelInputChanged() {
      this.panel.firstLoad = false;
    },
    cancelWarning() {
      this.panel.firstLoad = false;
      this.dialog = false;
    }
  }
};
</script>
